<template>
  <div class="job-application-form" data-aos="fade">
    <Transition name="fade" mode="out-in">
      <form class="job-application-form__form" v-if="!success">
        <div class="job-application-form__row">
          <TextInput
            class="job-application-form__input"
            v-model="model.firstName"
            placeholder="First Name"
            required
            :error="errors.firstName"
            icon="fa-user"
          />
          <TextInput
            class="job-application-form__input"
            v-model="model.lastName"
            placeholder="Last Name"
            required
            :error="errors.lastName"
            icon="fa-user"
          />
        </div>
        <TextInput
          class="job-application-form__input"
          v-model="model.email"
          placeholder="E-Mail"
          required
          :error="errors.email"
          icon="fa-envelope"
        />
        <TextInput
          class="job-application-form__input"
          v-model="model.phoneNumber"
          placeholder="Phone Number"
          :error="errors.phoneNumber"
          icon="fa-phone"
        />
        <TextInput
          class="job-application-form__input"
          v-model="model.linkedInProfileUrl"
          placeholder="Linked In"
          :error="errors.linkedInProfileUrl"
          icon="fa-brands fa-linkedin"
        />
        <TextInput
          class="job-application-form__input"
          v-model="model.portfolioUrl"
          placeholder="Link to Portfolio"
          :error="errors.portfolioUrl"
          icon="fa-globe"
        />
        <TextInput
          class="job-application-form__input"
          v-model="model.telegramUsername"
          placeholder="Telegram Username"
          :error="errors.telegramUsername"
          icon="fa-brands fa-telegram"
        />
        <FileInput
          class="job-application-form__input"
          v-model="model.cvFiles"
          label="CV / Resume"
          :error="errors.cvFiles"
          icon="fa-file"
          accept=".pdf"
          required
        />
        <!--        <TextInput-->
        <!--            class="job-application-form__input"-->
        <!--            v-model="model.coverLetter"-->
        <!--            placeholder="Cover Letter"-->
        <!--            :error="errors.coverLetter"-->
        <!--            type="textarea"-->
        <!--        />-->
        <div class="job-application-form__submit-button">
          <Button @click.prevent="submit">
            Apply
          </Button>
        </div>
      </form>
      <div class="job-application-form__submit-feedback" v-else>
        <p class="job-application-form__feedback-title">Thank you!</p>
        <p class="job-application-form__feedback-text">
          Thanks a lot for getting in touch with us!
          We've received your application.
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type {PropertiesToType} from "@common/types/utility";
import type {Base64File} from "@website/components/inputs/FileInput.vue";

const {createApplication} = useCareersClient();

export interface JobApplicationFormProps {
  jobId: number
}

const props = defineProps<JobApplicationFormProps>()

const model = reactive<{
  firstName: string | null,
  lastName: string | null,
  email: string | null,
  phoneNumber: string | null,
  telegramUsername: string | null,
  linkedInProfileUrl: string | null,
  portfolioUrl: string | null,
  cvFiles: Base64File[] | null,
  coverLetter: string | null,
}>({
  firstName: null,
  lastName: null,
  email: null,
  phoneNumber: null,
  telegramUsername: null,
  linkedInProfileUrl: null,
  portfolioUrl: null,
  cvFiles: null,
  coverLetter: null,
})
const errors = reactive<Partial<PropertiesToType<typeof model, string>>>({})
const success = ref<boolean>(false);

async function submit() {
  if (
    model.firstName == null ||
    model.lastName == null ||
    model.email == null ||
    model.cvFiles == null
  ) {
    errors.firstName = model.firstName ? undefined : 'Required';
    errors.lastName = model.lastName ? undefined : 'Required';
    errors.email = model.email ? undefined : 'Required';
    errors.cvFiles = model.cvFiles && model.cvFiles.length ? undefined : 'Required';
    success.value = false;
    return;
  }

  const result = await createApplication({
    jobId: props.jobId,
    candidate: {
      fullName: model.firstName + ' ' + model.lastName,
      email: model.email,
      phoneNumber: model.phoneNumber ?? undefined,
      telegramUsername: model.telegramUsername ?? undefined,
      linkedInProfileUrl: model.linkedInProfileUrl ?? undefined,
      portfolioUrl: model.portfolioUrl ?? undefined,
      coverLetter: model.coverLetter ?? undefined,
      base64CvFile: model.cvFiles[0]!.base64Encoded,
    }
  })

  errors.firstName = result?.validationErrors.candidate?.fullName?._errors[0];
  errors.lastName = result?.validationErrors.candidate?.fullName?._errors[0];
  errors.email = result?.validationErrors.candidate?.email?._errors[0];
  errors.phoneNumber = result?.validationErrors.candidate?.phoneNumber?._errors[0];
  errors.telegramUsername = result?.validationErrors.candidate?.telegramUsername?._errors[0];
  errors.linkedInProfileUrl = result?.validationErrors.candidate?.linkedInProfileUrl?._errors[0];
  errors.portfolioUrl = result?.validationErrors.candidate?.portfolioUrl?._errors[0];
  errors.coverLetter = result?.validationErrors.candidate?.coverLetter?._errors[0];
  errors.cvFiles = result?.validationErrors.candidate?.base64CvFile?._errors[0];

  if (result?.validationErrors) {
    success.value = false;
  } else {
    success.value = true;
  }
}
</script>

<style lang="scss">
.job-application-form {
  &__form {
    display: flex;
    flex-direction: column;
    gap: 8px;

    width: 100%;
  }

  &__row {
    display: flex;
    gap: 8px;

    .job-application-form__input {
      flex: 1 0 0;
    }

    @media (max-width: $md) {
      flex-direction: column;
    }
  }

  &__input {
    width: 100%;
  }

  &__submit-button {
    margin-top: 8px;
    align-self: center;
  }

  &__sent-indicator {
    margin-top: 4px;

    color: #2ca615;
    font-family: var(--font-family-main);
    font-size: 16px;
    font-weight: 500;
  }

  &__submit-feedback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    text-align: center;

    min-height: 200px;
  }

  &__feedback-title {
    color: #212529;
    font-family: var(--font-family-main);
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    text-transform: capitalize;

    @media (max-width: $md) {
      font-size: 32px;
    }
  }

  &__feedback-text {
    color: #212529;
    font-family: var(--font-family-main);
    font-size: 16px;
    font-weight: 400;

    max-width: 400px;
  }
}
</style>